import { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import workspaceService from "api/workspaceService";
import { loadUiPrivileges } from "api/userService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import {
  BUDGETS_ACCESS_OPTIONS,
  EBudgetsAccessLevel,
  EUserRole,
  LOCAL_LAW_OPTIONS,
  SPECIAL_CASE_OPTIONS,
  USER_ROLE_OPTIONS,
} from "dto/Access/IMemberAccess";
import { ELocalLawAccess } from "dto/LocalLaw/ILocalLaw";
import { EWorkspaceJobTitleKind } from "dto/Workspace/IWorkspaceJobTitle";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

// Metryka pracownika — port Kwantum.Administration.Settings.View.EditUserModal z Magistratu:
// miejsce w strukturze (komórka, stanowisko, stopień służbowy ze słownika firmy) oraz
// uprawnienia, z których BE liczy widoczność funkcji w menu (rola, prawo miejscowe, widok
// budżetu, KSeF, programy dziedzinowe). Zapis jednym PUT /workspace/member.

// ErrorResponse z BE: errorCodes[0].message (np. „Właściciel firmy jest zawsze administratorem").
const errorText = (e: unknown, fallback: string) => {
  const data = (
    e as {
      response?: {
        data?: { errorCodes?: { message?: string }[]; Message?: string };
      };
    }
  )?.response?.data;
  return data?.errorCodes?.[0]?.message ?? data?.Message ?? fallback;
};

const ALL_LOCAL_LAW =
  ELocalLawAccess.ManageResolutions |
  ELocalLawAccess.ManageOrdinances |
  ELocalLawAccess.BasicManagement;

// Modal leży na z-index 9999999 (nad oknem Ustawień), a listy MUI renderują się w portalu
// na body z z-index 1300 (menu Select) / 9999 (popper Autocomplete, global.scss) — bez
// podbicia rozwijały się POD nakładką. Ta sama wartość co w UserWorkItemsTransferModal.
const POPUP_Z_INDEX = 10000000;
const selectMenuProps = { sx: { zIndex: POPUP_Z_INDEX } };

const MemberMetricModal: FC<Props> = ({
  member,
  structure,
  refetch,
  toggle,
}) => {
  const { mutate: editMember, isLoading: isSaving } =
    workspaceService.useWorkspaceEditMember();
  const { data: jobTitlesData } = workspaceService.useGetJobTitles();

  const isOwner = !!member.IsOwner;
  const isInvitation = !member.UserId;

  const [structureId, setStructureId] = useState<string>(
    member.StructureId ?? "",
  );
  const [positionTitle, setPositionTitle] = useState<string>(
    member.PositionTitle ?? "",
  );
  const [jobGradeId, setJobGradeId] = useState<string>(
    member.JobGradeId ?? "",
  );
  const [userRole, setUserRole] = useState<EUserRole>(
    isOwner ? EUserRole.Admin : (member.UserRole ?? EUserRole.Normal),
  );
  const [localLaw, setLocalLaw] = useState<number>(member.LocalLawAccess ?? 0);
  const [budgetsAccess, setBudgetsAccess] = useState<EBudgetsAccessLevel>(
    member.BudgetsAccessLevel ?? EBudgetsAccessLevel.Default,
  );
  const [specialCases, setSpecialCases] = useState<number>(
    member.SpecialCasePermissions ?? 0,
  );
  const [isKSeFHandler, setIsKSeFHandler] = useState<boolean>(
    !!member.IsKSeFHandler,
  );

  // Administrator (i właściciel) ma pełne prawo miejscowe z definicji — jak w BE.
  const hasFullLocalLaw = isOwner || userRole === EUserRole.Admin;

  // Komórki w kolejności drzewa z wcięciem.
  const units = useMemo(() => {
    const out: { id: string; label: string; depth: number }[] = [];
    const walk = (parentId: string | null, depth: number) => {
      structure
        .filter((i) => (i.ParentId ?? null) === parentId)
        .sort((a, b) => a.Name.localeCompare(b.Name, "pl"))
        .forEach((i) => {
          out.push({
            id: i.Id,
            label: i.Symbol ? `${i.Symbol} · ${i.Name}` : i.Name,
            depth,
          });
          walk(i.Id, depth + 1);
        });
    };
    walk(null, 0);
    return out;
  }, [structure]);

  const jobTitles = jobTitlesData?.data?.Items ?? [];

  // Stopnie: aktywne + ewentualnie zarchiwizowany, który członek już ma.
  const grades = jobTitles.filter(
    (t) =>
      t.Kind === EWorkspaceJobTitleKind.Grade &&
      (!t.IsArchived || t.Id === member.JobGradeId),
  );
  const positions = jobTitles
    .filter((t) => t.Kind === EWorkspaceJobTitleKind.Position && !t.IsArchived)
    .map((t) => t.Name);

  const roleHint = USER_ROLE_OPTIONS.find((o) => o.value === userRole)?.hint;

  const toggleLocalLaw = (flag: ELocalLawAccess) => {
    setLocalLaw((current) => {
      if (current & flag) {
        return current & ~flag;
      }
      // Magistrat (EditUserViewModel): „Nadzór ogólny" obejmuje uchwały i zarządzenia —
      // wyklucza się z pozostałymi flagami.
      if (flag === ELocalLawAccess.BasicManagement) {
        return ELocalLawAccess.BasicManagement;
      }
      return (current & ~ELocalLawAccess.BasicManagement) | flag;
    });
  };

  const toggleSpecialCase = (flag: number) =>
    setSpecialCases((current) =>
      current & flag ? current & ~flag : current | flag,
    );

  const handleSave = () => {
    editMember(
      {
        Id: member.Id,
        PositionTitle: positionTitle.trim(),
        StructureId: structureId || null,
        SetStructure: (member.StructureId ?? "") !== structureId,
        JobGradeId: jobGradeId || null,
        SetJobGrade: (member.JobGradeId ?? "") !== jobGradeId,
        UserRole: isOwner ? undefined : userRole,
        LocalLawAccess: hasFullLocalLaw || isInvitation ? undefined : localLaw,
        BudgetsAccessLevel: budgetsAccess,
        SpecialCasePermissions: specialCases,
        IsKSeFHandler: isKSeFHandler,
      },
      {
        onSuccess: () => {
          refetch();
          // Własna metryka zmienia własne menu — przelicz je bez przeładowania.
          if (member.IsMe) {
            loadUiPrivileges();
          }
          toggle();
        },
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Metryka pracownika",
            text: errorText(e, "Nie udało się zapisać metryki."),
          }),
      },
    );
  };

  return ReactDOM.createPortal(
    <div className={styles.modal}>
      <div className={styles.window}>
        <div className={styles.header}>
          <div className={styles.title}>Metryka pracownika</div>
          <div className={styles.subtitle}>
            {member.Label}
            {member.Email && member.Email !== member.Label
              ? ` · ${member.Email}`
              : ""}
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.sectionTitle}>Miejsce w strukturze</div>
          <FormControl size="small" fullWidth>
            <InputLabel>Komórka organizacyjna</InputLabel>
            <Select
              label="Komórka organizacyjna"
              value={structureId}
              onChange={(e) => setStructureId(e.target.value as string)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">
                <em>Brak przypisania</em>
              </MenuItem>
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  <span style={{ paddingLeft: u.depth * 16 }}>{u.label}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            size="small"
            options={positions}
            inputValue={positionTitle}
            onInputChange={(_, value) => setPositionTitle(value)}
            // Inline, nie sx: global.scss ma .MuiPopper-root { z-index: 9999 } o tej samej
            // specyficzności co klasa z sx — styl inline wygrywa niezależnie od kolejności arkuszy.
            slotProps={{ popper: { style: { zIndex: POPUP_Z_INDEX } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Stanowisko służbowe"
                inputProps={{ ...params.inputProps, maxLength: 200 }}
              />
            )}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Stopień służbowy</InputLabel>
            <Select
              label="Stopień służbowy"
              value={jobGradeId}
              onChange={(e) => setJobGradeId(e.target.value as string)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="">
                <em>Brak</em>
              </MenuItem>
              {grades.map((g) => (
                <MenuItem key={g.Id} value={g.Id}>
                  {g.Name}
                  {g.IsArchived ? " (archiwalny)" : ""}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Słownik stopni: Ustawienia → Zespół → Stopnie/Stanowiska.
            </FormHelperText>
          </FormControl>

          <div className={styles.sectionTitle}>Uprawnienia</div>
          <FormControl size="small" fullWidth disabled={isOwner}>
            <InputLabel>Rola</InputLabel>
            <Select
              label="Rola"
              value={userRole}
              onChange={(e) => setUserRole(Number(e.target.value) as EUserRole)}
              MenuProps={selectMenuProps}
            >
              {USER_ROLE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {isOwner
                ? "Właściciel firmy jest zawsze administratorem."
                : roleHint}
            </FormHelperText>
          </FormControl>

          <div className={styles.group}>
            <div className={styles.groupLabel}>Prawo miejscowe</div>
            {LOCAL_LAW_OPTIONS.map((o) => (
              <FormControlLabel
                key={o.flag}
                className={styles.checkbox}
                control={
                  <Checkbox
                    size="small"
                    checked={
                      hasFullLocalLaw
                        ? !!(ALL_LOCAL_LAW & o.flag)
                        : !!(localLaw & o.flag)
                    }
                    disabled={hasFullLocalLaw || isInvitation}
                    onChange={() => toggleLocalLaw(o.flag)}
                  />
                }
                label={o.label}
              />
            ))}
            {(hasFullLocalLaw || isInvitation) && (
              <FormHelperText>
                {hasFullLocalLaw
                  ? "Administrator prowadzi wszystkie akty prawa miejscowego."
                  : "Uprawnienia prawa miejscowego można nadać po dołączeniu osoby do firmy."}
              </FormHelperText>
            )}
          </div>

          <FormControl size="small" fullWidth>
            <InputLabel>Widok budżetu</InputLabel>
            <Select
              label="Widok budżetu"
              value={budgetsAccess}
              onChange={(e) =>
                setBudgetsAccess(Number(e.target.value) as EBudgetsAccessLevel)
              }
              MenuProps={selectMenuProps}
            >
              {BUDGETS_ACCESS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            {budgetsAccess === EBudgetsAccessLevel.Department &&
              userRole !== EUserRole.Superior && (
                <FormHelperText>
                  Budżet wydziału widzi kierownik — dla pozostałych ról działa
                  jak „Tylko własny budżet”.
                </FormHelperText>
              )}
          </FormControl>

          <FormControlLabel
            className={styles.checkbox}
            control={
              <Checkbox
                size="small"
                checked={isKSeFHandler}
                onChange={(e) => setIsKSeFHandler(e.target.checked)}
              />
            }
            label="Dekretuje faktury KSeF"
          />

          <div className={styles.group}>
            <div className={styles.groupLabel}>Programy dziedzinowe</div>
            <div className={styles.flags}>
              {/* Podpowiedź w natywnym title: dymek MUI (global.scss: z-index 99999 !important)
                  schowałby się pod nakładką modala. */}
              {SPECIAL_CASE_OPTIONS.map((o) => (
                <FormControlLabel
                  key={o.flag}
                  title={o.hint}
                  className={styles.checkbox}
                  control={
                    <Checkbox
                      size="small"
                      checked={!!(specialCases & o.flag)}
                      onChange={() => toggleSpecialCase(o.flag)}
                    />
                  }
                  label={o.label}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.buttons}>
          <HollowButton onClick={() => toggle()} text="Anuluj" />
          <Button onClick={handleSave} text="Zapisz" disabled={isSaving} />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MemberMetricModal;
